/**
 * Topic Builder Utility
 * Mirrors the C# ProcessEngineEnvelopeFactory.BuildTopic logic
 * Used to construct consistent topic paths for execution events
 *
 * Format: process-engine.{entityType}.{action}
 * Examples:
 * - process-engine.process.started
 * - process-engine.process-thread.executing
 * - process-engine.process-element.completed
 * - process-engine.error.errored
 */

/**
 * ExecutionEventType enum mapping
 * Mirrors: eExecutionEventType in C#
 */
export enum ExecutionEventType {
  App = 'app',
  Process = 'process',
  ProcessThread = 'process-thread',
  ProcessElement = 'process-element',
  Error = 'error',
}

/**
 * ExecutionEventSubType enum mapping
 * Mirrors: eExecutionEventSubType in C#
 */
export enum ExecutionEventSubType {
  Starting = 'starting',
  Executing = 'executing',
  Completed = 'completed',
  Progressing = 'progressing',
  Errored = 'errored',
}

/**
 * Maps C# eExecutionEventType enum values to entity type strings
 * @param eventType - The C# eExecutionEventType enum value as string
 * @returns The entity type string for topic building
 */
export function mapEventTypeToEntity(eventType: string | undefined): string {
  if (!eventType) return 'unknown';

  switch (eventType.toLowerCase()) {
    case 'process':
      return 'process';
    case 'processthread':
      return 'process-thread';
    case 'processelement':
      return 'process-element';
    case 'app':
      return 'app';
    case 'error':
      return 'error';
    default:
      return 'unknown';
  }
}

/**
 * Maps C# eExecutionEventSubType enum values to action strings
 * @param subType - The C# eExecutionEventSubType enum value as string
 * @returns The action string for topic building
 */
export function mapSubTypeToAction(subType: string | undefined): string {
  if (!subType) return 'unknown';

  switch (subType.toLowerCase()) {
    case 'starting':
      return 'started';
    case 'executing':
      return 'executing';
    case 'completed':
      return 'completed';
    case 'progressing':
      return 'progressing';
    case 'errored':
      return 'errored';
    default:
      return 'unknown';
  }
}

/**
 * Builds a topic path from event type and sub-type
 * Mirrors: ProcessEngineEnvelopeFactory.BuildTopic logic in C#
 *
 * @param eventType - The eExecutionEventType value (as string or enum)
 * @param subType - The eExecutionEventSubType value (as string or enum)
 * @returns The constructed topic path: "process-engine.{entity}.{action}"
 *
 * @example
 * buildTopic('Process', 'Starting') // → "process-engine.process.started"
 * buildTopic('ProcessElement', 'Completed') // → "process-engine.process-element.completed"
 * buildTopic('Error', 'Errored') // → "process-engine.error.errored"
 */
export function buildTopic(
  eventType: string | undefined,
  subType: string | undefined
): string {
  if (!eventType || !subType) {
    return 'process-engine.unknown';
  }

  const entity = mapEventTypeToEntity(eventType);
  const action = mapSubTypeToAction(subType);

  return `process-engine.${entity}.${action}`;
}

/**
 * Parses a topic string back into its components
 * Reverse of buildTopic()
 *
 * @param topic - The topic string to parse
 * @returns Object with entity and action, or null if invalid
 *
 * @example
 * parseTopic('process-engine.process.started')
 * // → { entity: 'process', action: 'started' }
 */
export function parseTopic(
  topic: string | undefined
): { entity: string; action: string } | null {
  if (!topic || !topic.startsWith('process-engine.')) {
    return null;
  }

  const parts = topic.split('.');
  if (parts.length !== 3) {
    return null;
  }

  return {
    entity: parts[1],
    action: parts[2],
  };
}
